import { useCallback, useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import { Zap, Search, Trash2, Pause, Play, Clock, Package } from "lucide-react";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

/** Preset durasi (cooldown) flash sale dalam menit. */
const DURATION_PRESETS = [
  { label: "30 Menit", value: 30 },
  { label: "1 Jam", value: 60 },
  { label: "3 Jam", value: 180 },
  { label: "6 Jam", value: 360 },
  { label: "12 Jam", value: 720 },
  { label: "1 Hari", value: 1440 },
  { label: "3 Hari", value: 4320 },
  { label: "7 Hari", value: 10080 },
];

const STATUS_STYLE = {
  live: { label: "Berjalan", cls: "bg-[#FF003C] text-white" },
  scheduled: { label: "Terjadwal", cls: "bg-[#FFD700] text-black" },
  sold_out: { label: "Stok Habis", cls: "bg-white/15 text-white/70" },
  ended: { label: "Selesai", cls: "bg-white/10 text-white/50" },
  stopped: { label: "Dihentikan", cls: "bg-white/10 text-white/50" },
};

/** Sisa waktu dalam format "2h 14m 03s" (atau "-" kalau sudah lewat). */
function useCountdown(endsAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!endsAt) return "-";
  const diff = new Date(endsAt).getTime() - now;
  if (diff <= 0) return "Berakhir";
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}h ${h}j ${m}m`;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function Countdown({ endsAt }) {
  const text = useCountdown(endsAt);
  return <span className="font-mono text-[#00F0FF]">{text}</span>;
}

/**
 * Form pemasangan flash sale untuk satu produk seller yang sedang live.
 *
 * Harga flash dihitung ulang di server; preview di sini murni membantu admin
 * melihat hasil potongan sebelum menekan tombol.
 */
function CreateForm({ product, onDone, onCancel }) {
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(20);
  const [stock, setStock] = useState(Math.min(5, product.stock));
  const [duration, setDuration] = useState(60);
  // Batas pembelian per ID game: mencegah satu orang memborong promo dengan
  // ID/akun game yang sama. Server tetap yang menegakkan aturannya saat checkout.
  const [limitPerGameId, setLimitPerGameId] = useState(true);
  const [maxPerGameId, setMaxPerGameId] = useState(1);
  const [saving, setSaving] = useState(false);


  const preview = useMemo(() => {
    const price = Number(product.price) || 0;
    const val = Number(discountValue) || 0;
    const raw = discountType === "percentage" ? price - (price * val) / 100 : price - val;
    // Server memakai batas yang sama: harga flash tidak boleh 0 atau negatif.
    return Math.max(100, Math.round(raw));
  }, [product.price, discountType, discountValue]);

  const percentOff = product.price > 0 ? Math.round(((product.price - preview) / product.price) * 100) : 0;

  const submit = async () => {
    if (Number(stock) < 1) return toast.error("Stok flash sale minimal 1");
    if (Number(stock) > product.stock) return toast.error(`Stok produk hanya ${product.stock}`);
    setSaving(true);
    try {
      await api.post("/admin/flash-sales", {
        product_id: product.id,
        discount_type: discountType,
        discount_value: Number(discountValue),
        stock: Number(stock),
        duration_minutes: Number(duration),
        limit_per_game_id: limitPerGameId,
        max_per_game_id: Math.max(1, Number(maxPerGameId) || 1),
      });

      toast.success("Flash sale dipasang!");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memasang flash sale");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="flash-create-form" className="border border-[#FF003C]/40 bg-[#FF003C]/5 p-4 mt-2 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/60">Jenis Potongan</label>
          <div className="flex gap-2 mt-2">
            {[
              { id: "percentage", label: "Persen (%)" },
              { id: "fixed", label: "Nominal (Rp)" },
            ].map((o) => (
              <button
                key={o.id}
                data-testid={`flash-discount-type-${o.id}`}
                onClick={() => setDiscountType(o.id)}
                className={`flex-1 px-3 py-2 text-xs uppercase font-bold tracking-widest border ${
                  discountType === o.id ? "border-[#FF003C] bg-[#FF003C]/20 text-white" : "border-white/15 text-white/60"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
            Besar Potongan {discountType === "percentage" ? "(%)" : "(Rp)"}
          </label>
          <input
            data-testid="flash-discount-value"
            type="number"
            min="1"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#FF003C] text-white text-sm px-3 py-2 mt-2"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/60">
            Stok Flash Sale (maks {product.stock})
          </label>
          <input
            data-testid="flash-stock"
            type="number"
            min="1"
            max={product.stock}
            value={stock}
            onChange={(e) => setStock(e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#FF003C] text-white text-sm px-3 py-2 mt-2"
          />
          <p className="text-[10px] text-white/40 mt-1">
            Setelah jatah ini habis, produk otomatis kembali ke harga normal.
          </p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-white/60">Durasi (Cooldown)</label>
          <select
            data-testid="flash-duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className="w-full bg-black border border-white/15 focus:border-[#FF003C] text-white text-sm px-3 py-2 mt-2"
          >
            {DURATION_PRESETS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <p className="text-[10px] text-white/40 mt-1">Hitung mundur berjalan sejak flash sale dipasang.</p>
        </div>

        {/* Batas per ID game — pembeli yang memakai ID/akun game sama tidak bisa
            memborong jatah promo. Dicek ulang di server saat checkout. */}
        <div className="md:col-span-2 border-t border-white/10 pt-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid="flash-limit-per-game-id"
              onClick={() => setLimitPerGameId((v) => !v)}
              className={`px-3 py-2 text-[10px] uppercase font-black tracking-widest border ${
                limitPerGameId
                  ? "border-[#FF003C] bg-[#FF003C]/20 text-white"
                  : "border-white/15 text-white/50"
              }`}
            >
              {limitPerGameId ? "Batas per ID Game: Aktif" : "Batas per ID Game: Nonaktif"}
            </button>
            {limitPerGameId && (
              <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/60">
                Maks. pembelian / ID
                <input
                  data-testid="flash-max-per-game-id"
                  type="number"
                  min="1"
                  value={maxPerGameId}
                  onChange={(e) => setMaxPerGameId(e.target.value)}
                  className="w-20 bg-black border border-white/15 focus:border-[#FF003C] text-white text-sm px-2 py-2"
                />
              </label>
            )}
          </div>
          <p className="text-[10px] text-white/40 mt-1">
            Satu ID/akun game hanya boleh membeli produk ini{" "}
            {limitPerGameId ? `${Math.max(1, Number(maxPerGameId) || 1)}x` : "tanpa batas"} selama flash
            sale berlangsung. Nonaktifkan bila promo boleh dibeli berulang.
          </p>
        </div>
      </div>


      <div className="flex items-center justify-between border-t border-white/10 pt-3 flex-wrap gap-3">
        <div className="text-sm">
          <span className="text-white/40 line-through mr-2">{formatIDR(product.price)}</span>
          <span data-testid="flash-preview-price" className="font-display font-black text-xl text-[#FF003C]">
            {formatIDR(preview)}
          </span>
          <span className="ml-2 text-[10px] font-black uppercase tracking-widest bg-[#FF003C] text-white px-1.5 py-0.5">
            -{percentOff}%
          </span>
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="px-4 py-2 border border-white/15 text-white/60 text-xs uppercase font-bold tracking-widest hover:bg-white/5">
            Batal
          </button>
          <button
            data-testid="flash-create-submit"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-[#FF003C] hover:bg-[#FF3363] text-white text-xs uppercase font-black tracking-widest disabled:opacity-50"
          >
            {saving ? "Memasang..." : "Pasang Flash Sale"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Baris flash sale aktif/lampau beserta aksi kelola. */
function FlashRow({ fs, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [editStock, setEditStock] = useState(fs.stock);
  const style = STATUS_STYLE[fs.status] || STATUS_STYLE.ended;

  const patch = async (body, successMsg) => {
    setBusy(true);
    try {
      await api.patch(`/admin/flash-sales/${fs.id}`, body);
      toast.success(successMsg);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memperbarui flash sale");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Hapus flash sale ini? Produk langsung kembali ke harga normal.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/flash-sales/${fs.id}`);
      toast.success("Flash sale dihapus");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`flash-row-${fs.id}`} className="border border-white/10 p-4 flex flex-col md:flex-row md:items-center gap-4">
      <div className="w-16 h-16 bg-black border border-white/10 overflow-hidden shrink-0">
        {fs.product?.image_url && (
          <img src={resolveImg(fs.product.image_url)} alt={fs.product_title} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 ${style.cls}`}>
            {style.label}
          </span>
          <h4 className="font-bold text-white truncate">{fs.product_title}</h4>
          {fs.product_deleted && <span className="text-[10px] text-[#FF003C]">produk dihapus</span>}
        </div>
        <p className="text-xs text-white/50 mt-0.5">{fs.seller_name}</p>
        <div className="flex items-center gap-3 mt-1 text-sm flex-wrap">
          <span className="text-white/40 line-through text-xs">{formatIDR(fs.original_price)}</span>
          <span className="font-display font-bold text-[#FF003C]">{formatIDR(fs.flash_price)}</span>
          <span className="text-[10px] font-black uppercase bg-[#FF003C]/20 text-[#FF6B8A] px-1.5 py-0.5">
            -{fs.discount_percent}%
          </span>
          <span className="text-xs text-white/50 flex items-center gap-1">
            <Package className="w-3 h-3" /> {fs.sold}/{fs.stock} terjual · sisa {fs.remaining}
          </span>
          {fs.status === "live" && (
            <span className="text-xs flex items-center gap-1">
              <Clock className="w-3 h-3 text-[#00F0FF]" /> <Countdown endsAt={fs.ends_at} />
            </span>
          )}
          {fs.limit_per_game_id && (
            <span
              data-testid={`flash-limit-badge-${fs.id}`}
              title="Satu ID/akun game dibatasi jumlah pembeliannya selama promo"
              className="text-[10px] font-black uppercase tracking-widest bg-[#00F0FF]/15 text-[#00F0FF] px-1.5 py-0.5"
            >
              maks {fs.max_per_game_id || 1}x / ID game
            </span>
          )}
        </div>

        <div className="h-1 bg-white/10 mt-2 max-w-xs">
          <div className="h-full bg-[#FF003C]" style={{ width: `${fs.sold_percent}%` }} />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <input
            data-testid={`flash-edit-stock-${fs.id}`}
            type="number"
            min={fs.sold}
            value={editStock}
            onChange={(e) => setEditStock(e.target.value)}
            className="w-20 bg-black border border-white/15 text-white text-xs px-2 py-2"
          />
          <button
            onClick={() => patch({ stock: Number(editStock) }, "Stok flash sale diperbarui")}
            disabled={busy || Number(editStock) === fs.stock}
            className="px-2 py-2 border border-white/15 text-white/70 text-[10px] uppercase font-bold tracking-widest hover:bg-white/5 disabled:opacity-40"
          >
            Set
          </button>
        </div>
        {fs.status === "live" || fs.status === "scheduled" ? (
          <button
            data-testid={`flash-stop-${fs.id}`}
            onClick={() => patch({ active: false }, "Flash sale dihentikan")}
            disabled={busy}
            className="px-3 py-2 border border-white/15 text-white/70 text-[10px] uppercase font-bold tracking-widest hover:bg-white/5 flex items-center gap-1"
          >
            <Pause className="w-3 h-3" /> Stop
          </button>
        ) : (
          <button
            data-testid={`flash-resume-${fs.id}`}
            onClick={() => patch({ active: true, duration_minutes: 60 }, "Flash sale dijalankan lagi (1 jam)")}
            disabled={busy || fs.product_deleted}
            className="px-3 py-2 border border-white/15 text-white/70 text-[10px] uppercase font-bold tracking-widest hover:bg-white/5 flex items-center gap-1 disabled:opacity-40"
          >
            <Play className="w-3 h-3" /> Jalankan
          </button>
        )}
        <button
          data-testid={`flash-delete-${fs.id}`}
          onClick={del}
          disabled={busy}
          className="p-2 text-white/40 hover:text-[#FF003C]"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Tab admin untuk mengelola Flash Sale.
 *
 * Bagian atas: daftar produk seller yang sedang live (stok ada & toko buka)
 * yang bisa dipasang flash sale. Bagian bawah: daftar flash sale beserta
 * status, sisa waktu, dan progres penjualan.
 */
export default function AdminFlashSale() {
  const [candidates, setCandidates] = useState([]);
  const [flashes, setFlashes] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async (search = "") => {
    setLoading(true);
    try {
      const [c, f] = await Promise.all([
        api.get("/admin/flash-sales/candidates", { params: search ? { q: search } : {} }),
        api.get("/admin/flash-sales"),
      ]);
      setCandidates(c.data);
      setFlashes(f.data);
    } catch {
      toast.error("Gagal memuat data flash sale");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    setOpenForm(null);
    load(q);
  };

  const visibleFlashes = flashes.filter((f) => statusFilter === "all" || f.status === statusFilter);
  const liveCount = flashes.filter((f) => f.status === "live").length;

  return (
    <div data-testid="admin-flash-sale-tab" className="space-y-8">
      <div>
        <h2 className="font-display font-black text-2xl text-white flex items-center gap-2">
          <Zap className="w-6 h-6 text-[#FF003C]" /> Kelola Flash Sale
        </h2>
        <p className="text-xs text-white/50 mt-1">
          Pasang potongan harga berbatas waktu & stok untuk produk seller yang sedang live.
          Setelah stok flash habis atau waktunya selesai, produk otomatis kembali ke harga normal.
          <span className="text-[#FF003C] font-bold ml-1">{liveCount} sedang berjalan.</span>
        </p>
      </div>

      {/* --- Pilih produk --- */}
      <div className="border border-white/10 p-5">
        <h3 className="font-display font-bold text-lg text-white mb-3">Pilih Produk Seller (Live)</h3>
        <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="flash-candidate-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(q)}
              placeholder="Cari nama produk..."
              className="w-full bg-black border border-white/15 focus:border-[#FF003C] text-white text-sm pl-9 pr-3 py-2"
            />
          </div>
          <button
            onClick={() => load(q)}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs uppercase font-bold tracking-widest"
          >
            Cari
          </button>
        </div>

        {loading ? (
          <p className="text-white/50 text-sm">Memuat...</p>
        ) : candidates.length === 0 ? (
          <p className="text-white/50 text-sm">Tidak ada produk live yang cocok.</p>
        ) : (
          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
            {candidates.map((p) => (
              <div key={p.id} data-testid={`flash-candidate-${p.id}`}>
                <div className="border border-white/10 p-3 flex items-center gap-3">
                  <div className="w-12 h-12 bg-black border border-white/10 overflow-hidden shrink-0">
                    {p.image_url && <img src={resolveImg(p.image_url)} alt={p.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-white truncate">{p.title}</h4>
                    <p className="text-[11px] text-white/50">
                      {p.seller_name} · stok {p.stock} · terjual {p.sold}
                    </p>
                    <p className="text-sm font-display font-bold text-[#00F0FF]">{formatIDR(p.price)}</p>
                  </div>
                  {p.active_flash_sale ? (
                    <span className="text-[10px] font-black uppercase tracking-widest bg-[#FF003C] text-white px-2 py-1">
                      Sedang Flash Sale
                    </span>
                  ) : (
                    <button
                      data-testid={`flash-set-${p.id}`}
                      onClick={() => setOpenForm(openForm === p.id ? null : p.id)}
                      className="px-3 py-2 bg-[#FF003C] hover:bg-[#FF3363] text-white text-[10px] uppercase font-black tracking-widest flex items-center gap-1"
                    >
                      <Zap className="w-3 h-3" /> Set Flash Sale
                    </button>
                  )}
                </div>
                {openForm === p.id && (
                  <CreateForm product={p} onDone={refresh} onCancel={() => setOpenForm(null)} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Daftar flash sale --- */}
      <div className="border border-white/10 p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-display font-bold text-lg text-white">Daftar Flash Sale</h3>
          <div className="flex gap-1 flex-wrap">
            {[
              { id: "all", label: "Semua" },
              { id: "live", label: "Berjalan" },
              { id: "sold_out", label: "Habis" },
              { id: "ended", label: "Selesai" },
              { id: "stopped", label: "Dihentikan" },
            ].map((s) => (
              <button
                key={s.id}
                data-testid={`flash-filter-${s.id}`}
                onClick={() => setStatusFilter(s.id)}
                className={`px-3 py-1.5 text-[10px] uppercase font-bold tracking-widest border ${
                  statusFilter === s.id ? "border-[#FF003C] text-[#FF003C]" : "border-white/15 text-white/50"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        {visibleFlashes.length === 0 ? (
          <p className="text-white/50 text-sm">Belum ada flash sale pada filter ini.</p>
        ) : (
          <div className="space-y-2">
            {visibleFlashes.map((fs) => (
              <FlashRow key={fs.id} fs={fs} onChanged={refresh} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
